import { useEffect, useRef, useSyncExternalStore } from 'react';

// Whether the app currently believes it has no signal. Set as soon as the phone reports it's
// offline or a request fails without reaching the server; cleared once a quick check of /health
// gets through. While it's set, reads skip the network and go straight to saved data (see
// AxiosInterceptor), so pages don't sit behind a spinner waiting for a request to give up.

const PROBE_EVERY_MS = 15000;
const PROBE_TIMEOUT_MS = 4000;
// Android doesn't always fire the `offline` event in an installed app, so the phone's own
// online flag is also checked every few seconds. Costs nothing: no request is made.
const WATCH_EVERY_MS = 3000;

let noSignal = typeof navigator !== 'undefined' && navigator.onLine === false;
let probeTimer = null;
const listeners = new Set();
const signalBackListeners = new Set();

const emit = () => listeners.forEach(l => l());

export const hasNoSignal = () => noSignal;

export function markNoSignal() {
  if (noSignal) return;
  noSignal = true;
  emit();
  startProbing();
}

export function markSignal() {
  if (!noSignal) return;
  noSignal = false;
  stopProbing();
  emit();
  signalBackListeners.forEach(l => l());
}

async function probe() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch('/health', { cache: 'no-store', signal: controller.signal });
    if (res.ok) markSignal();
  } catch {
    // still no signal
  } finally {
    clearTimeout(timer);
  }
}

function startProbing() {
  if (!probeTimer) probeTimer = setInterval(probe, PROBE_EVERY_MS);
}

function stopProbing() {
  clearInterval(probeTimer);
  probeTimer = null;
}

function watch() {
  if (navigator.onLine === false) markNoSignal();
  else if (noSignal) probe();
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', markNoSignal);
  window.addEventListener('online', probe);
  setInterval(() => { if (!noSignal && navigator.onLine === false) markNoSignal(); }, WATCH_EVERY_MS);
  // Coming back to the app (after toggling airplane mode, say): check straight away
  document.addEventListener('visibilitychange', () => { if (!document.hidden) watch(); });
  if (noSignal) startProbing();
}

const subscribe = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };

export const useNoSignal = () => useSyncExternalStore(subscribe, hasNoSignal);

// Runs `callback` each time the signal comes back, so a page showing its saved copy can fetch
// fresh data. Always calls the latest callback, so it sees current state.
export function useOnSignalBack(callback) {
  const ref = useRef(callback);
  ref.current = callback;
  useEffect(() => {
    const listener = () => ref.current?.();
    signalBackListeners.add(listener);
    return () => signalBackListeners.delete(listener);
  }, []);
}

// Which saved copy the current page is showing, if any (e.g. "gigs" saved at 23:05), so the one
// app-wide banner can say so. Pages report it through <OfflineBanner>.
let savedCopy = null;
const savedCopyListeners = new Set();

export function reportSavedCopy(copy) {
  savedCopy = copy;
  savedCopyListeners.forEach(l => l());
  return () => {
    if (savedCopy !== copy) return;
    savedCopy = null;
    savedCopyListeners.forEach(l => l());
  };
}

const subscribeSavedCopy = (listener) => { savedCopyListeners.add(listener); return () => savedCopyListeners.delete(listener); };

export const useSavedCopy = () => useSyncExternalStore(subscribeSavedCopy, () => savedCopy);
